import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import {GENDERS, type Gender} from '../../domain/gender.value-object';
import type {WritableBodyMetrics} from '../../domain/user-profile';

const MAX_HEIGHT_CM = 300;

export type BodyMetricsFormValues = {
  readonly birthdate: string;
  readonly gender: Gender | '';
  readonly heightCm: string;
};

export class BodyMetricsValidator implements Validator<BodyMetricsFormValues> {
  readonly standardSchema = z.object({
    birthdate: z
      .string()
      .refine(
        value => value === '' || /^\d{4}-\d{2}-\d{2}$/.test(value),
        'Birthdate must be a complete date.',
      ),
    gender: z.enum([...GENDERS, '']),
    heightCm: z
      .string()
      .refine(
        value => {
          const trimmed = value.trim();
          return trimmed === '' || (!Number.isNaN(Number(trimmed)) && Number(trimmed) > 0);
        },
        'Height must be a positive number.',
      )
      .refine(value => {
        const trimmed = value.trim();
        return trimmed === '' || Number(trimmed) <= MAX_HEIGHT_CM;
      }, `Height must be at most ${MAX_HEIGHT_CM} cm.`),
  });

  validate(value: unknown): Result<BodyMetricsFormValues, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(
        new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid body metrics.'),
      );
    }
    return ok(parsed.data);
  }
}

export const bodyMetricsFormToWritable = (values: BodyMetricsFormValues): WritableBodyMetrics => {
  const heightRaw = values.heightCm.trim();
  return {
    birthdate: values.birthdate === '' ? null : values.birthdate,
    gender: values.gender === '' ? null : values.gender,
    heightCm: heightRaw === '' ? null : Number(heightRaw),
  };
};
